import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { format } from 'date-fns';
import { SessionState, MaterializedView, SlackChannel, SlackMessage, ScenarioDefinition } from '@/types';
import { api } from '@/services/api';
import { useUIStore, useSessionStore } from '@/stores';
import {
  Hash, MessageSquare, Send, Users, AtSign,
} from 'lucide-react';

interface SlackInterfaceProps {
  session: SessionState;
  materializedView: MaterializedView;
  scenario?: ScenarioDefinition | null;
}

interface MentionableNpc {
  id: string;
  name: string;
}

// NPC avatar photos (matching dashboard NPC cards)
const NPC_AVATARS: Record<string, string> = {
  // Inbox Crisis
  'jennifer-martinez': 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=80&h=80&fit=crop&crop=face',
  'marcus-johnson': 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&h=80&fit=crop&crop=face',
  'sarah-chen': 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=80&h=80&fit=crop&crop=face',
  // The Handover
  'priya-devi': 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=80&h=80&fit=crop&crop=face',
  'aanya-joshi': 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=80&h=80&fit=crop&crop=face',
  'hugo-lindqvist': 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=80&h=80&fit=crop&crop=face',
  'greg-whittaker': 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=80&h=80&fit=crop&crop=face',
};

// Map display name → NPC ID for avatar lookup. Now scenario-aware: takes the list it should
// search rather than relying on a module-level constant pinned to one scenario.
const getNpcIdFromName = (name: string, npcs: MentionableNpc[]): string | null => {
  const npc = npcs.find(n => n.name.toLowerCase() === name.toLowerCase());
  return npc?.id || null;
};

// Deterministic color from sender name (same pattern as EmailInterface)
const getSenderColor = (senderId: string): string => {
  const colors = [
    'bg-blue-600', 'bg-purple-600', 'bg-pink-600', 'bg-red-600',
    'bg-orange-600', 'bg-amber-600', 'bg-teal-600', 'bg-cyan-600',
  ];
  let hash = 0;
  for (let i = 0; i < senderId.length; i++) {
    hash = senderId.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

const getSenderInitials = (senderId: string): string => {
  const parts = senderId.split(' ');
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return senderId.substring(0, 2).toUpperCase();
};

// Basic Slack-style formatting: *bold*, _italic_, `code`, @mentions.
// Mentionable NPCs are passed in so the highlight respects the active scenario.
const formatSlackText = (text: string, mentionableNpcs: MentionableNpc[] = []): React.ReactNode => {
  const parts: React.ReactNode[] = [];
  let key = 0;

  const regex = /(\*[^*]+\*)|(_[^_]+_)|(`[^`]+`)|(@[\w][\w\s]*[\w])/g;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }

    const matched = match[0];
    if (matched.startsWith('*') && matched.endsWith('*')) {
      parts.push(<strong key={key++}>{matched.slice(1, -1)}</strong>);
    } else if (matched.startsWith('_') && matched.endsWith('_')) {
      parts.push(<em key={key++}>{matched.slice(1, -1)}</em>);
    } else if (matched.startsWith('`') && matched.endsWith('`')) {
      parts.push(
        <code key={key++} className="bg-slate-900 px-1.5 py-0.5 rounded text-blue-400 text-xs">
          {matched.slice(1, -1)}
        </code>
      );
    } else if (matched.startsWith('@')) {
      const mentionName = matched.slice(1);
      const isKnownNpc = mentionableNpcs.some(n =>
        n.name.toLowerCase() === mentionName.toLowerCase()
      );
      parts.push(
        <span key={key++} className={`px-0.5 rounded ${isKnownNpc ? 'bg-blue-500/20 text-blue-400' : 'bg-slate-700 text-slate-300'} font-medium`}>
          {matched}
        </span>
      );
    }

    lastIndex = match.index + matched.length;
  }

  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts.length > 0 ? parts : text;
};

const SlackInterface: React.FC<SlackInterfaceProps> = ({
  session,
  materializedView,
  scenario,
}) => {
  // Mentionable NPCs derived from the active scenario (was previously a hardcoded
  // module-level constant pinned to inbox-crisis NPCs).
  const mentionableNpcs = useMemo<MentionableNpc[]>(
    () => (scenario?.npcs ?? []).map(n => ({ id: n.id, name: n.name })),
    [scenario]
  );
  const { addNotification, selectedChannelId, selectedChannelType, setSelectedChannel } = useUIStore();
  const { setSession, setMaterializedView } = useSessionStore();
  const [messageInput, setMessageInput] = useState('');
  const [sending, setSending] = useState(false);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionIndex, setMentionIndex] = useState(0);
  const feedEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Filter NPCs matching the current @ query
  const mentionResults = useMemo(() => {
    if (mentionQuery === null) return [];
    if (mentionQuery === '') return mentionableNpcs;
    const q = mentionQuery.toLowerCase();
    return mentionableNpcs.filter(n => n.name.toLowerCase().includes(q));
  }, [mentionQuery, mentionableNpcs]);

  // Extract NPC IDs from @mentions in message text
  const extractMentionIds = useCallback((text: string): string[] => {
    const ids: string[] = [];
    const mentionRegex = /@([\w][\w\s]*[\w])/g;
    let m;
    while ((m = mentionRegex.exec(text)) !== null) {
      const name = m[1].toLowerCase();
      const npc = mentionableNpcs.find(n => n.name.toLowerCase() === name);
      if (npc) ids.push(npc.id);
    }
    return [...new Set(ids)];
  }, []);

  const channels = materializedView.slack.channels;
  const directMessages = materializedView.slack.directMessages;

  // Auto-select first channel if none selected
  useEffect(() => {
    if (!selectedChannelId) {
      const channelIds = Object.keys(channels);
      if (channelIds.length > 0) {
        setSelectedChannel(channelIds[0], 'channel');
      }
    }
  }, [selectedChannelId, channels, setSelectedChannel]);

  // Get messages for current selection
  const activeMessages = useMemo((): SlackMessage[] => {
    if (!selectedChannelId) return [];
    if (selectedChannelType === 'dm') {
      return directMessages[selectedChannelId] || [];
    }
    return channels[selectedChannelId]?.messages || [];
  }, [selectedChannelId, selectedChannelType, channels, directMessages]);

  // Get active channel info
  const activeChannel = useMemo((): SlackChannel | null => {
    if (!selectedChannelId || selectedChannelType !== 'channel') return null;
    return channels[selectedChannelId] || null;
  }, [selectedChannelId, selectedChannelType, channels]);

  // Scroll to bottom when messages change
  useEffect(() => {
    feedEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [activeMessages.length]);

  // Channel unread counts (all messages are "unread" since we don't track reads for Slack)
  const channelMessageCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const [id, channel] of Object.entries(channels)) {
      counts[id] = channel.messages.length;
    }
    return counts;
  }, [channels]);

  const dmMessageCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const [id, msgs] of Object.entries(directMessages)) {
      counts[id] = msgs.length;
    }
    return counts;
  }, [directMessages]);

  // Get display name for DM key
  const getDMDisplayName = useCallback((dmKey: string): string => {
    const msgs = directMessages[dmKey];
    if (msgs && msgs.length > 0) {
      const otherPerson = msgs.find(m => m.from !== 'player');
      if (otherPerson) return otherPerson.from;
    }
    // Fallback: convert key to title case
    return dmKey.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  }, [directMessages]);

  const handleSendMessage = useCallback(async () => {
    if (!messageInput.trim() || !selectedChannelId || sending) return;

    try {
      setSending(true);
      const mentionedNpcIds = extractMentionIds(messageInput);
      await api.sendSlackMessage(session.id, {
        channelId: selectedChannelId,
        content: messageInput.trim(),
        isDirectMessage: selectedChannelType === 'dm',
        mentionedNpcIds: mentionedNpcIds.length > 0 ? mentionedNpcIds : undefined,
      });
      setMessageInput('');
      setMentionQuery(null);

      // Refetch MV so the sent message appears immediately
      const stateResponse = await api.getSessionState(session.id);
      setSession(stateResponse.session);
      setMaterializedView(stateResponse.materializedView);
    } catch (error) {
      console.error('Error sending Slack message:', error);
      addNotification({ type: 'error', message: 'Failed to send message' });
    } finally {
      setSending(false);
    }
  }, [messageInput, selectedChannelId, selectedChannelType, session.id, sending, addNotification, setSession, setMaterializedView, extractMentionIds]);

  const insertMention = useCallback((npc: { id: string; name: string }) => {
    if (!inputRef.current) return;
    const el = inputRef.current;
    const pos = el.selectionStart;
    const text = messageInput;

    // Find the @ that started this mention
    let atPos = pos - 1;
    while (atPos >= 0 && text[atPos] !== '@') atPos--;

    const before = text.substring(0, atPos);
    const after = text.substring(pos);
    const newText = `${before}@${npc.name} ${after}`;
    setMessageInput(newText);
    setMentionQuery(null);
    setMentionIndex(0);

    // Restore cursor after the inserted mention
    setTimeout(() => {
      const newPos = atPos + npc.name.length + 2; // @ + name + space
      el.selectionStart = newPos;
      el.selectionEnd = newPos;
      el.focus();
    }, 0);
  }, [messageInput]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    setMessageInput(text);

    // Detect @ mention trigger
    const pos = e.target.selectionStart;
    const textBefore = text.substring(0, pos);
    const atMatch = textBefore.match(/@(\w*)$/);
    if (atMatch) {
      setMentionQuery(atMatch[1]);
      setMentionIndex(0);
    } else {
      setMentionQuery(null);
    }
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    // Mention popup navigation
    if (mentionQuery !== null && mentionResults.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionIndex(i => (i + 1) % mentionResults.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionIndex(i => (i - 1 + mentionResults.length) % mentionResults.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insertMention(mentionResults[mentionIndex]);
        return;
      }
      if (e.key === 'Escape') {
        setMentionQuery(null);
        return;
      }
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  }, [handleSendMessage, mentionQuery, mentionResults, mentionIndex, insertMention]);

  // Check if two messages should be grouped (same sender, within 5 minutes)
  const shouldGroupWithPrevious = (msg: SlackMessage, prevMsg: SlackMessage | null): boolean => {
    if (!prevMsg) return false;
    if (msg.from !== prevMsg.from) return false;
    const timeDiff = new Date(msg.timestamp).getTime() - new Date(prevMsg.timestamp).getTime();
    return timeDiff < 5 * 60 * 1000; // 5 minutes
  };

  const channelDisplayName = selectedChannelType === 'dm' && selectedChannelId
    ? getDMDisplayName(selectedChannelId)
    : selectedChannelId;

  return (
    <div className="flex h-full bg-slate-800 text-white font-sans">
      {/* Channel Sidebar */}
      <div className="w-64 bg-slate-900 border-r border-slate-600 flex flex-col">
        {/* Sidebar Header */}
        <div className="p-4 border-b border-slate-600">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-blue-400" />
            <h2 className="text-lg font-bold text-white">Slack</h2>
          </div>
        </div>

        {/* Channels */}
        <div className="flex-1 overflow-y-auto p-2">
          <div className="mb-4">
            <h3 className="px-3 py-1.5 text-xs font-bold text-slate-400 uppercase tracking-wider">
              Channels
            </h3>
            {Object.values(channels).map((channel) => (
              <button
                key={channel.id}
                onClick={() => setSelectedChannel(channel.id, 'channel')}
                className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors ${
                  selectedChannelId === channel.id && selectedChannelType === 'channel'
                    ? 'bg-slate-900 text-blue-400 font-semibold'
                    : 'text-slate-300 hover:bg-slate-900/50 hover:text-white'
                }`}
              >
                <Hash className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">{channel.name}</span>
                {channelMessageCounts[channel.id] > 0 && (
                  <span className="ml-auto text-[10px] bg-blue-500/20 text-blue-400 px-1.5 py-0.5 rounded-full font-bold">
                    {channelMessageCounts[channel.id]}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Direct Messages */}
          {Object.keys(directMessages).length > 0 && (
            <div>
              <h3 className="px-3 py-1.5 text-xs font-bold text-slate-400 uppercase tracking-wider">
                Direct Messages
              </h3>
              {Object.keys(directMessages).map((dmKey) => {
                const displayName = getDMDisplayName(dmKey);
                return (
                  <button
                    key={dmKey}
                    onClick={() => setSelectedChannel(dmKey, 'dm')}
                    className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors ${
                      selectedChannelId === dmKey && selectedChannelType === 'dm'
                        ? 'bg-slate-900 text-blue-400 font-semibold'
                        : 'text-slate-300 hover:bg-slate-900/50 hover:text-white'
                    }`}
                  >
                    {(() => {
                      const npcId = getNpcIdFromName(displayName, mentionableNpcs);
                      const avatarUrl = npcId ? NPC_AVATARS[npcId] : null;
                      return avatarUrl ? (
                        <img src={avatarUrl} alt={displayName} className="w-5 h-5 rounded-full object-cover flex-shrink-0" />
                      ) : (
                        <div className={`w-5 h-5 rounded-full ${getSenderColor(displayName)} flex items-center justify-center text-[8px] font-bold text-white flex-shrink-0`}>
                          {getSenderInitials(displayName)}
                        </div>
                      );
                    })()}
                    <span className="truncate">{displayName}</span>
                    {dmMessageCounts[dmKey] > 0 && (
                      <span className="ml-auto text-[10px] bg-blue-500/20 text-blue-400 px-1.5 py-0.5 rounded-full font-bold">
                        {dmMessageCounts[dmKey]}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Message Feed + Input */}
      <div className="flex-1 flex flex-col">
        {/* Channel Header */}
        {selectedChannelId && (
          <div className="px-6 py-3 border-b border-slate-600 flex items-center gap-3">
            {selectedChannelType === 'channel' ? (
              <>
                <Hash className="w-5 h-5 text-slate-300" />
                <div>
                  <h2 className="text-base font-bold text-white">{selectedChannelId}</h2>
                  {activeChannel?.topic && (
                    <p className="text-xs text-slate-400">{activeChannel.topic}</p>
                  )}
                </div>
                {activeChannel?.members && activeChannel.members.length > 0 && (
                  <div className="ml-auto flex items-center gap-1 text-xs text-slate-400">
                    <Users className="w-3.5 h-3.5" />
                    <span>{activeChannel.members.length}</span>
                  </div>
                )}
              </>
            ) : (
              <>
                <AtSign className="w-5 h-5 text-slate-300" />
                <h2 className="text-base font-bold text-white">{channelDisplayName}</h2>
              </>
            )}
          </div>
        )}

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {activeMessages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <MessageSquare className="w-12 h-12 text-slate-600 mb-4" />
              <p className="text-slate-300 text-sm">
                {selectedChannelId ? 'No messages yet' : 'Select a channel'}
              </p>
            </div>
          ) : (
            <div className="space-y-0.5">
              {activeMessages.map((msg, idx) => {
                const prevMsg = idx > 0 ? activeMessages[idx - 1] : null;
                const isGrouped = shouldGroupWithPrevious(msg, prevMsg);
                const isPlayer = msg.from === 'player';

                return (
                  <div
                    key={msg.id}
                    className={`group hover:bg-slate-900/30 rounded px-2 ${
                      isGrouped ? 'py-0.5' : 'py-2 mt-2'
                    }`}
                  >
                    {!isGrouped ? (
                      <div className="flex items-start gap-3">
                        {(() => {
                          const npcId = getNpcIdFromName(msg.from, mentionableNpcs);
                          const avatarUrl = npcId ? NPC_AVATARS[npcId] : null;
                          return avatarUrl ? (
                            <img src={avatarUrl} alt={msg.from} className="w-9 h-9 rounded-lg object-cover flex-shrink-0" />
                          ) : (
                            <div className={`w-9 h-9 rounded-lg ${getSenderColor(msg.from)} flex items-center justify-center text-xs font-bold text-white flex-shrink-0`}>
                              {getSenderInitials(msg.from)}
                            </div>
                          );
                        })()}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline gap-2 mb-0.5">
                            <span className={`text-sm font-bold ${isPlayer ? 'text-blue-400' : 'text-white'}`}>
                              {isPlayer ? 'You' : msg.from}
                            </span>
                            <span className="text-xs text-slate-500">
                              {format(new Date(msg.timestamp), 'h:mm a')}
                            </span>
                          </div>
                          <div className="text-sm text-slate-200 leading-relaxed whitespace-pre-wrap">
                            {formatSlackText(msg.content, mentionableNpcs)}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start gap-3">
                        <div className="w-9 flex-shrink-0 flex items-center justify-center">
                          <span className="text-[10px] text-transparent group-hover:text-slate-500 transition-colors">
                            {format(new Date(msg.timestamp), 'h:mm')}
                          </span>
                        </div>
                        <div className="text-sm text-slate-200 leading-relaxed whitespace-pre-wrap">
                          {formatSlackText(msg.content, mentionableNpcs)}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              <div ref={feedEndRef} />
            </div>
          )}
        </div>

        {/* Message Input */}
        {selectedChannelId && (
          <div className="px-6 pb-4 relative">
            {/* @ Mention Autocomplete Popup */}
            {mentionQuery !== null && mentionResults.length > 0 && (
              <div className="absolute bottom-full left-6 right-6 mb-1 bg-slate-900 border border-slate-600 rounded-lg shadow-xl overflow-hidden z-20">
                <div className="px-3 py-1.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-700">
                  People
                </div>
                {mentionResults.map((npc, idx) => (
                  <button
                    key={npc.id}
                    onClick={() => insertMention(npc)}
                    className={`w-full flex items-center gap-3 px-3 py-2 text-sm transition-colors ${
                      idx === mentionIndex ? 'bg-blue-500/20 text-blue-400' : 'text-slate-200 hover:bg-slate-800'
                    }`}
                  >
                    {NPC_AVATARS[npc.id] ? (
                      <img src={NPC_AVATARS[npc.id]} alt={npc.name} className="w-6 h-6 rounded object-cover" />
                    ) : (
                      <div className={`w-6 h-6 rounded ${getSenderColor(npc.name)} flex items-center justify-center text-[9px] font-bold text-white`}>
                        {getSenderInitials(npc.name)}
                      </div>
                    )}
                    <span className="font-medium">{npc.name}</span>
                  </button>
                ))}
              </div>
            )}
            <div className="flex items-end gap-2 bg-slate-900 border border-slate-600 rounded-lg p-2 focus-within:ring-2 focus-within:ring-blue-500/50 focus-within:border-blue-500">
              <textarea
                ref={inputRef}
                value={messageInput}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder={
                  selectedChannelType === 'channel'
                    ? `Message #${selectedChannelId} — type @ to mention someone`
                    : `Message ${channelDisplayName}`
                }
                rows={1}
                className="flex-1 bg-transparent text-white placeholder-slate-400 text-sm resize-none focus:outline-none min-h-[24px] max-h-[120px]"
                style={{ height: 'auto' }}
                onInput={(e) => {
                  const target = e.target as HTMLTextAreaElement;
                  target.style.height = 'auto';
                  target.style.height = Math.min(target.scrollHeight, 120) + 'px';
                }}
              />
              <button
                onClick={handleSendMessage}
                disabled={!messageInput.trim() || sending}
                className="p-1.5 rounded-md bg-blue-500 hover:bg-blue-500/90 disabled:bg-blue-500/30 disabled:cursor-not-allowed transition-colors flex-shrink-0"
              >
                <Send className="w-4 h-4 text-white" />
              </button>
            </div>
            <p className="text-[10px] text-slate-500 mt-1 px-1">
              Press Enter to send, Shift+Enter for new line, @ to mention
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SlackInterface;
